import React, { useState } from 'react';

import { withEventScope } from '@repo/event-processor';

import {
  EmptyContainer,
  CarouselContainer,
  CarouselCell,
  ItemsWrapper,
} from './styles';
import type { CarouselProps } from './types';

// On desktop devices scroll by dragging doesn't work out-of-the-box and we have to implement it manually
const applyOnlyForMouse = (
  event: React.PointerEvent<HTMLDivElement>,
  cb: (e: React.PointerEvent<HTMLDivElement>) => void
): void => {
  if (event.pointerType === 'mouse') {
    cb(event);
  }
};

const Carousel: React.FC<CarouselProps> = ({
  items,
  emptyMessage = 'No items found',
  gap = 24,
  itemWidth,
  snapScrollType,
  centered,
  style,
}) => {
  const [dragging, setDragging] = useState(false);
  const [screenX, setScreenX] = useState<number | null>(null);

  const handlePointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ): void => {
    const target = event.target as HTMLDivElement;
    if (!dragging || typeof screenX !== 'number' || !target) {
      return;
    }

    // Due to Safari bug https://bugs.webkit.org/show_bug.cgi?id=255207 we have to calc movementX by our own
    // in accordance to the MDN https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/movementX
    // If movementX > 0 => we scroll to the start / left => we need to decrease the scrolled size
    const movementX = event.screenX - screenX;

    if (!movementX) {
      return;
    }

    if (snapScrollType === undefined) {
      target.scrollTo({ left: target.scrollLeft - movementX });
      setScreenX(event.screenX);
    } else {
      target.scrollTo({
        left: target.scrollLeft + (movementX < 0 ? 1 : -1) * (itemWidth + gap),
        behavior: 'smooth',
      });
      handlePointerUp(event);
    }
  };

  const handlePointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ): void => {
    if (dragging) {
      return;
    }
    const target = event.target as HTMLDivElement;
    target.setPointerCapture(event.pointerId);
    setScreenX(event.screenX);
    setDragging(true);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    (event.target as HTMLDivElement).releasePointerCapture(event.pointerId);
    setScreenX(null);
    setDragging(false);
  };

  if (!items?.length) {
    return <EmptyContainer>{emptyMessage}</EmptyContainer>;
  }

  return (
    <CarouselContainer
      dragging={dragging}
      style={style}
      tabIndex={0}
      onLostPointerCapture={(e: React.PointerEvent<HTMLDivElement>): void =>
        applyOnlyForMouse(e, handlePointerUp)
      }
      onPointerDown={(e: React.PointerEvent<HTMLDivElement>): void =>
        applyOnlyForMouse(e, handlePointerDown)
      }
      onPointerMove={(e: React.PointerEvent<HTMLDivElement>): void =>
        applyOnlyForMouse(e, handlePointerMove)
      }
      onPointerUp={(e: React.PointerEvent<HTMLDivElement>): void =>
        applyOnlyForMouse(e, handlePointerUp)
      }
    >
      <ItemsWrapper
        centered={centered}
        gap={gap}
        itemWidth={itemWidth}
        snapScrollType={snapScrollType}
      >
        {items.map(({ id, element }) => (
          <CarouselCell key={id} centered={centered} itemWidth={itemWidth}>
            {element}
          </CarouselCell>
        ))}
      </ItemsWrapper>
    </CarouselContainer>
  );
};

export default withEventScope(Carousel, { module: 'carousel' });
